/* eslint-disable complexity */
import styled from '@emotion/styled';
import {Container, media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {IntroHeader} from 'UI/components/layout/IntoHeader';
import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {PayloadLexicalSmall} from 'UI/components/layout/PayloadLexicalSmall';
import {H4} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';
import {Card} from 'UI/components/modules/Steps/Card';
import {PartnerCard} from 'UI/components/modules/Steps/PartnerCard';
import {SwmCard} from 'UI/components/modules/Steps/SwmCard';

import {FadeInAnimation} from 'UI/animations/shared';

import {isNonEmptyLexical} from 'Application/utils/typeGuards';
import {IlluEigentuemer, IlluInternet, Lightbulb} from 'Images/icons';

import type {StepsType} from 'types/payload-types';

/**
 * The props for the `Steps` component, which renders a structured section
 * containing a multi-step process, SWM service card, partner information,
 * and optional intro/outro content.
 * This interface defines the data required for each subsection, including
 * step-by-step cards, partner cards, and optional descriptive text blocks.
 * It also supports a `testId` for automated testing.
 */
interface ComponentProps {
    /**
     * An optional flag to hide the entire block when set to true.
     * When `true`, the component renders nothing.
     *
     * @default false
     */
    hideBlock?: StepsType['hideBlock'];
    /**
     * An optional identifier applied as the `id` attribute of the intro header.
     * Useful for in-page navigation and accessibility (e.g., anchor links).
     */
    id?: StepsType['id'];
    /**
     * Optional intro content for the section, typically containing a headline,
     * overline, and introductory description.
     */
    intro?: StepsType['intro'];
    /**
     * Optional outro content displayed after the steps and partner sections.
     * May include descriptive text and/or an informational text block with an icon.
     */
    outro?: StepsType['outro'];
    /**
     * Configuration for the partner section, including a headline, description,
     * and a list of partner cards.
     */
    partners?: StepsType['partners'];
    /**
     * Configuration for the step-by-step section, including headline, description,
     * and an array of step cards to display in a grid.
     */
    steps?: StepsType['steps'];
    /**
     * Configuration for the SWM card, including hotline details, button, image,
     * and descriptive text.
     */
    swm?: StepsType['swm'];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Steps'
     */
    testId?: string;
}

/**
 * A composite section component that displays a multi-step process, partner information,
 * and optional intro/outro content.
 * The `Steps` component renders a structured flow of content starting with an `IntroHeader`,
 * followed by a step-by-step section, an SWM-specific card, and a partner section. Each step
 * is displayed using `Card` components inside a responsive grid, while the SWM card uses
 * the `SwmCard` component to highlight hotline information and a CTA. The partner section
 * is rendered with a headline, optional description, and a grid of `PartnerCard` components.
 * Finally, the component supports an outro section containing an optional outro text block
 * and an informational box with an icon. This provides additional context or disclaimers
 * at the end of the flow.
 * The `testId` prop is applied as a `data-cy` attribute for reliable end-to-end test targeting.
 *
 * @param props           The component props.
 * @param props.hideBlock An optional flag to hide the entire block when set to true. Defaults to `false`.
 * @param props.id        The unique identifier for the section, used for linking and accessibility.
 * @param props.intro     Optional intro content configuration passed to `IntroHeader`.
 * @param props.outro     Optional outro content with outro text and/or informational text.
 * @param props.partners  Configuration for the partner section, including headline, description, and a list of partner cards.
 * @param props.steps     Configuration for the step-by-step section, including headline, description, and an array of step cards.
 * @param props.swm       Configuration for the SWM card, including hotline details, button, image, and description.
 * @param props.testId    An optional identifier used for testing, applied as `data-cy`. Defaults to `'Steps'`.
 * @returns A JSX element rendering the multi-step process, SWM card, partner cards, and optional outro content.
 *
 * @example
 * ```tsx
 * <Steps
 *   id="installation-steps"
 *   intro={{ headline: "So funktioniert's", overline: "Schritte", text: introText }}
 *   steps={{
 *     headline: "Ihre Schritte",
 *     description: stepsDescription,
 *     stepCards: [
 *       { id: "1", headline: "Anfrage stellen", text: step1Text },
 *       { id: "2", headline: "Beratung erhalten", text: step2Text }
 *     ]
 *   }}
 *   swm={{
 *     headline: "Unser Service",
 *     hotlineText: "Rufen Sie uns an:",
 *     hotline: { label: "0800 123 456", linkType: "phone", url: "tel:0800123456" },
 *     button: { label: "Kostenlos beauftragen", linkType: "internal", internal: "/order" },
 *     image: swmImage,
 *     description: swmDescription
 *   }}
 *   partners={{
 *     headline: "Unsere Partner",
 *     description: partnersDescription,
 *     partnerCards: [
 *       { id: "p1", headline: "Partner A", hotline: partnerHotline, button: partnerButton, image: partnerImage }
 *     ]
 *   }}
 *   outro={{
 *     outroText: outroText,
 *     infoText: infoBoxText
 *   }}
 * />
 * ```
 */
const Steps = observer(({
    hideBlock = false,
    id,
    intro,
    outro,
    partners,
    steps,
    swm,
    testId = 'Steps'
}: ComponentProps) => (hideBlock ? null : (
    <Container data-cy={testId}>
        <IntroHeader blockId={id} intro={intro} />
        <ViewportTrigger baseComponent={HeadlineWrapper}>
            <MotionIcon variants={FadeInAnimation} />
            <Spacer y={10} />
            <MotionH4 $align="center" as="h3" variants={FadeInAnimation}>{steps?.headline}</MotionH4>
            {steps?.description && (
                <>
                    <Spacer y={12} />
                    <motion.div variants={FadeInAnimation}>
                        <PayloadLexical description={steps.description} />
                    </motion.div>
                </>
            )}
        </ViewportTrigger>
        <Spacer y={12} />
        <ViewportTrigger baseComponent={StepGrid}>
            {steps?.stepCards.map((step, index) => (
                <Card key={step.id} headline={step.headline} index={index} text={step.text} />
            ))}
            <SwrWrapper>
                <Spacer y={{
                    xs: 0, md: 6
                }}
                />
                <SwmCard
                    button={swm?.button}
                    headline={swm?.headline}
                    hotlineLink={swm?.hotline}
                    hotlineText={swm?.hotlineText}
                    image={swm?.image}
                    text={swm?.description}
                />
            </SwrWrapper>
        </ViewportTrigger>
        <Spacer y={{
            xs: 18, md: 24
        }}
        />
        <ViewportTrigger baseComponent={HeadlineWrapper}>
            <MotionIcon2 variants={FadeInAnimation} />
            <Spacer y={10} />
            <MotionH4 $align="center" as="h3" variants={FadeInAnimation}>{partners?.headline}</MotionH4>
            {partners?.description && (
                <>
                    <Spacer y={{
                        xs: 6, md: 12
                    }}
                    />
                    <motion.div variants={FadeInAnimation}>
                        <PayloadLexical description={partners.description} />
                    </motion.div>
                </>
            )}
        </ViewportTrigger>
        <Spacer y={12} />
        <ViewportTrigger baseComponent={PartnerGrid}>
            {partners?.partnerCards.map(partner => (
                <PartnerCard
                    key={partner.id}
                    button={partner.button}
                    headline={partner.headline}
                    hotlineLink={partner.hotline}
                    hotlineText={partner.hotlineText}
                    image={partner.image}
                    partner={partner.partner}
                />
            ))}
        </ViewportTrigger>
        <ViewportTrigger baseComponent={BaseWrapper}>
            {outro?.outroText && isNonEmptyLexical(outro.outroText) && (
                <Description variants={FadeInAnimation}>
                    <Spacer y={12} />
                    <PayloadLexicalSmall description={outro.outroText} />
                </Description>
            )}
            {outro?.infoText && isNonEmptyLexical(outro.infoText) && (
                <>
                    <Spacer y={20} />
                    <InfoBox variants={FadeInAnimation}>
                        <StyledIcon />
                        <div>
                            <PayloadLexicalSmall description={outro.infoText} />
                        </div>
                    </InfoBox>
                </>
            )}
        </ViewportTrigger>
    </Container>
)));

Steps.displayName = 'Steps';

export {Steps};

const MotionIcon = styled(motion.create(IlluEigentuemer))``;

const MotionH4 = styled(motion.create(H4))``;

const SwrWrapper = styled.div`
    grid-column: 1 / -1;
`;

const MotionIcon2 = styled(motion.create(IlluInternet))``;

const Description = styled(motion.div)`
    margin-inline: auto;
    max-width: 68.4rem;
    text-align: center;

    & > p {
        text-align: center;
    }
`;

const InfoBox = styled(motion.div)`
    align-items: flex-start;
    border: 1px solid ${({theme}) => theme.colors.neutral200};
    border-radius: 24px;
    display: flex;
    gap: ${spacing(4)};
    margin-inline: auto;
    max-width: 92rem;
    padding: ${spacing(6)};
`;

const StyledIcon = styled(Lightbulb)`
    flex: 0 0 2rem;
`;

const HeadlineWrapper = styled.div`
    margin-inline: auto;
    max-width: 68.4rem;
    text-align: center;
    width: 100%;

    & > p {
        text-align: center;
    }
`;

const StepGrid = styled.div`
    display: grid;
    gap: ${spacing(6)};
    grid-template-columns: 1fr;

    ${media('md')} {
        grid-template-columns: 1fr 1fr;
    }

    ${media('xl')} {
        grid-template-columns: 1fr 1fr 1fr 1fr;
    }
`;

const PartnerGrid = styled.div`
    column-gap: ${spacing(6)};
    display: grid;
    grid-template-columns: 1fr;
    margin-inline: auto;
    max-width: 92rem;
    row-gap: ${spacing(6)};

    ${media('md')} {
        grid-template-columns: 1fr 1fr;
    }
`;

const BaseWrapper = styled.div``;